import { useId, useState, useMemo } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2Header } from '@pm2/design-tokens';
import { NotificationCount } from '@pm2/react-utils';
import { Tablist, Search, TabSection } from '@ui/kit';
import { HideFor, SizeTransition } from '@utils/react';
import { userAreaBreakpoints } from '../../../const.breakpoints.js';
import { getNavHeight } from '../../../utils/getNavHeight.js';
import { wrapper, tabListTitle, tabToggleNotificationBadge, searchWrapper, contentWrapper, content, transitionDurationMs } from '../shared.tablist.style.js';
import { useComponentEntrySelector, componentEntryStore } from '../state.js';
import { useTeacherEntries } from './hook.teacher-entries.js';
import { TeacherEntriesTable } from './table.root.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { filterComponentEntry } from '../utils/filter.js';
import { isNotificationConsideredRecent } from '../../notifications/util.is-considered-recent.js';

const selfCorrectingTabId = "self-correcting-tab";
const notSelfCorrectingTabId = "not-self-correcting-tab";
function TeacherTablist() {
  const baseId = `teacher-tablist-${useId()}`;
  const [selectedTabId, setSelectedTabId] = useState(selfCorrectingTabId);
  const filters = useComponentEntrySelector(state => state);
  const timespan = useComponentEntrySelector(state => state.filters.timespan);
  const rawEntries = useTeacherEntries(timespan);
  const selfCorrectingEntries = useMemo(() => rawEntries.selfCorrectingEntries.map(it => ({
    ...it,
    groupedEntries: it.groupedEntries.filter(entry => filterComponentEntry(filters, entry))
  })).filter(it => it.groupedEntries.length > 0), [filters, rawEntries.selfCorrectingEntries]);
  const selfCorrectingEntriesNotificationCount = selfCorrectingEntries.map(it => it.groupedEntries.filter(entry => entry.shareNotifications?.some(notifcation => isNotificationConsideredRecent(notifcation))).length).reduce((acc, curr) => acc + curr, 0);
  const notSelfCorrectingEntries = useMemo(() => rawEntries.notSelfCorrectingEntries.map(it => ({
    ...it,
    groupedEntries: it.groupedEntries.filter(entry => filterComponentEntry(filters, entry))
  })).filter(it => it.groupedEntries.length > 0), [filters, rawEntries.notSelfCorrectingEntries]);
  const notSelfCorrectingEntriesNotificationCount = notSelfCorrectingEntries.map(it => it.groupedEntries.filter(entry => entry.shareNotifications?.some(notifcation => isNotificationConsideredRecent(notifcation))).length).reduce((acc, curr) => acc + curr, 0);
  return /*#__PURE__*/jsxs("div", {
    className: wrapper,
    children: [/*#__PURE__*/jsx(Tablist, {
      baseId: baseId,
      label: "V\xE6lg sektion",
      tabs: [{
        id: selfCorrectingTabId,
        title: /*#__PURE__*/jsxs("div", {
          className: tabListTitle,
          children: [/*#__PURE__*/jsx("span", {
            children: "Selvrettende"
          }), /*#__PURE__*/jsx(NotificationCount, {
            className: tabToggleNotificationBadge,
            count: selfCorrectingEntriesNotificationCount,
            active: !!selfCorrectingEntriesNotificationCount
          })]
        })
      }, {
        id: notSelfCorrectingTabId,
        title: /*#__PURE__*/jsxs("div", {
          className: tabListTitle,
          children: [/*#__PURE__*/jsx("span", {
            children: "Ikke selvrettende"
          }), /*#__PURE__*/jsx(NotificationCount, {
            className: tabToggleNotificationBadge,
            count: notSelfCorrectingEntriesNotificationCount,
            active: !!notSelfCorrectingEntriesNotificationCount
          })]
        })
      }],
      selectedTabId: selectedTabId,
      onTabClick: setSelectedTabId
    }), /*#__PURE__*/jsx(HideFor, {
      className: searchWrapper,
      breakpoint: userAreaBreakpoints.mobile,
      children: /*#__PURE__*/jsx(Search, {
        placeholder: "S\xF8g i besvarelser",
        "aria-label": "S\xF8g i besvarelser",
        onQueryChange: newQuery => componentEntryStore.dispatch(componentEntryStore.actions.setQuery(newQuery)),
        debounceMs: 350,
        theme: "local",
        compact: true
      })
    }), /*#__PURE__*/jsx("div", {
      className: contentWrapper,
      children: /*#__PURE__*/jsx(TransitionGroup, {
        component: null,
        children: /*#__PURE__*/jsx(SizeTransition, {
          timeout: transitionDurationMs,
          classNames: content,
          swappable: true,
          axis: "vertical",
          getMinSizeInPx: () => ({
            height: getNavHeight() - pm2Header.getCurrentHeight() - (matchMedia(userAreaBreakpoints.mobile.toQuery()).matches ? 100 : 275)
          }),
          getMaxSizeInPx: () => ({
            height: window.innerHeight - pm2Header.getCurrentHeight() - (matchMedia(userAreaBreakpoints.mobile.toQuery()).matches ? 100 : 275)
          }),
          children: ref => /*#__PURE__*/jsx(TabSection, {
            ref: ref,
            className: content,
            baseId: baseId,
            selectedTabId: selectedTabId,
            children: selectedTabId === selfCorrectingTabId ? /*#__PURE__*/jsx(TeacherEntriesTable, {
              withStats: true,
              entries: selfCorrectingEntries
            }) : /*#__PURE__*/jsx(TeacherEntriesTable, {
              withStats: false,
              entries: notSelfCorrectingEntries
            })
          })
        }, `selected-tab-${selectedTabId}`)
      })
    })]
  });
}

export { TeacherTablist };
//# sourceMappingURL=molecule.tablist.js.map
